import { Formik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import { FullMeetingFragment, MeetingStepStatus, MeetingStepViewId, SelfreflectionQuestion } from '../../../../types'
import '../../selfreflection/selfreflection.less'
import { SelfReflectionTopicCardsView } from '../../selfreflection/SelfreflectionTopicCardsView'
import useSelfreflections from '../../selfreflection/useSelfreflections'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { useAuth } from '../../../../context/auth-context'
import { oneSelfreflectionValueExists } from '../../selfreflection/selfreflections.functions'
import { isUserHrForCountry } from '../../../../shared/rolesUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}
const ManagerPreparationSelfReflection: FC<Props> = ({ backBtn, nextBtn, meeting }) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const { data, error, loading } = useSelfreflections(meeting.employee.id, meeting.id)
  if (
    loading ||
    error ||
    !data ||
    (!data.selfreflectionQuestions && (data.selfreflectionQuestions as SelfreflectionQuestion[]).length > 0)
  ) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const initialValues = { form: data.selfreflectionQuestions as SelfreflectionQuestion[] }
  const isHrForCountry = isUserHrForCountry(meeting.appraisalSeason.country, user.roles)

  const meetingPrepared = meeting.employeePrepared
  const meetingShared = meeting.appraisal ? meeting.appraisal.selfReflectionShared : false
  const atLeastOneValueExist = oneSelfreflectionValueExists(data.selfreflectionQuestions)

  const notPrepared = !meetingPrepared || !atLeastOneValueExist
  const notShared = !notPrepared && !(meetingShared || isHrForCountry)
  const shared = !notPrepared && (meetingShared || isHrForCountry)

  const employeeFirstName = meeting.employee.chosenFirstName ? meeting.employee.chosenFirstName : meeting.employee.legalFirstName

  const renderTitle = () => {
    if (!meeting.employeePrepared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.notPrepared'
    } else if (shared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.shared'
    } else if (notShared) {
      return 'appraisalMeeting.stepSelfReflection.manager.heading.notShared'
    } else {
      return ''
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(_, { setSubmitting }) => {
        setSubmitting(false)
      }}
    >
      {({ isSubmitting }) => {
        return (
          <div data-e2e-manager-prep-self-reflection>
            <MeetingBackground>
              <PerText appearance="subtitle" color="primary" mb={!!renderTitle() ? 'm' : undefined}>
                {t(renderTitle(), {
                  employeeName: employeeFirstName,
                })}
              </PerText>

              {(notPrepared || notShared) && (
                <PerSpacer mb="xl">
                  <PerCardInfo
                    heading={t(
                      `appraisalMeeting.stepSelfReflection.manager.infoCard.heading.${notPrepared ? 'notPrepared' : 'notShared'}`,
                      {
                        employeeName: employeeFirstName,
                      },
                    )}
                    description={
                      notPrepared
                        ? t(`appraisalMeeting.stepSelfReflection.manager.infoCard.subheading.notPrepared`, {
                            employeeName: employeeFirstName,
                          })
                        : ''
                    }
                  />
                </PerSpacer>
              )}

              <PerText appearance="info" mb="xs">
                {notPrepared && t('appraisalMeeting.stepSelfReflection.manager.subheading.notPrepared')}
                {notShared && t('appraisalMeeting.stepSelfReflection.manager.subheading.notShared')}
                {shared && t('appraisalMeeting.stepSelfReflection.manager.subheading.shared')}
              </PerText>

              <SelfReflectionTopicCardsView meeting={meeting} isHrForCountry={isHrForCountry} />
            </MeetingBackground>

            <PerFlexRow justify="space-between">
              {backBtn && backBtn()}
              {nextBtn && nextBtn(MeetingStepStatus.Done, isSubmitting)}
            </PerFlexRow>
            <AppraisalFlowHelper stepViewId={MeetingStepViewId.SelfReflection} stepStatus={MeetingStepStatus.Done} />
          </div>
        )
      }}
    </Formik>
  )
}
export default ManagerPreparationSelfReflection
